/**
 * IndexedDB access (via idb). Two stores:
 *   - snapshots: autosave + explicit-save checkpoints (last 20 per doc uuid).
 *   - recent:    last-known content per document, for the "recent files" list.
 * All persistence is local; there is no server.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export type SnapshotKind = "auto" | "save";

export interface Snapshot {
  seq?: number;
  uuid: string;
  ts: number;
  xml: string;
  filename: string;
  kind: SnapshotKind;
}

export interface RecentEntry {
  uuid: string;
  filename: string;
  ts: number;
  xml: string;
}

interface ScriptPadDB extends DBSchema {
  snapshots: {
    key: number;
    value: Snapshot;
    indexes: { "by-uuid": string };
  };
  recent: {
    key: string;
    value: RecentEntry;
  };
}

const DB_NAME = "scriptpad";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<ScriptPadDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<ScriptPadDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ScriptPadDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const snaps = db.createObjectStore("snapshots", {
          keyPath: "seq",
          autoIncrement: true,
        });
        snaps.createIndex("by-uuid", "uuid");
        db.createObjectStore("recent", { keyPath: "uuid" });
      },
    });
  }
  return dbPromise;
}

/** Test-only: reset the cached connection (used with fake-indexeddb). */
export function _resetDbForTests(): void {
  dbPromise = null;
}
